import React from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiCheck, FiArrowRight, FiZap, FiCpu, FiStar } from 'react-icons/fi';

const distributionTiers = [
  { accounts: "20", price: "4,997", setup: "2,500" },
  { accounts: "50", price: "9,997", setup: "5,000", highlight: true },
  { accounts: "100", price: "17,997", setup: "7,500" }
];

const aiTiers = [
  { accounts: "20", price: "14,997", setup: "5,000" },
  { accounts: "50", price: "29,997", setup: "10,000", highlight: true },
  { accounts: "100", price: "49,997", setup: "15,000" }
];

const standaloneAssets = [
  { 
    name: "AI Influencer / Animated Video", 
    price: "200", 
    desc: "Custom character creation, voice cloning, and high-retention animation.",
    icon: FiZap
  },
  { 
    name: "AI Commercial / Ad Asset", 
    price: "800", 
    desc: "Professional grade commercial for ad spend. Optimized for conversion.",
    icon: FiStar
  }
];

export default function Pricing() {
  const scrollToContact = (e) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="pricing" className="py-32 bg-[#050505] px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter text-white mb-6 italic">
            Deployment <span className="text-[#DFFF00]">Tiers.</span>
          </h2>
          <p className="text-slate-500 font-bold uppercase tracking-[0.2em] text-sm italic">
            Industrial scale infrastructure priced per single network (TikTok, IG, or FB)
          </p>
        </div>

        {/* Standalone Assets */}
        <div className="mb-32">
          <div className="flex items-center gap-4 mb-12">
            <h3 className="text-white text-2xl font-black uppercase tracking-widest italic flex items-center gap-3 text-left">
              <SafeIcon icon={FiCpu} className="text-[#DFFF00]" /> 01. Standalone Creative
            </h3>
            <span className="h-px flex-grow bg-white/10" />
            <span className="text-slate-500 text-[10px] font-black uppercase tracking-widest">Pay Per Asset</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {standaloneAssets.map((asset, i) => (
              <div key={i} className="bg-[#0b0b0b] p-8 border border-white/10 flex flex-col md:flex-row items-center justify-between gap-8 group hover:border-[#DFFF00] transition-all">
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-[#DFFF00]/10">
                    <SafeIcon icon={asset.icon} className="text-[#DFFF00] text-2xl" />
                  </div>
                  <div>
                    <h4 className="text-white font-black uppercase tracking-tight text-xl mb-2 italic text-left">{asset.name}</h4>
                    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest max-w-sm text-left">{asset.desc}</p>
                  </div>
                </div>
                <div className="text-center md:text-right flex-shrink-0">
                  <div className="text-4xl font-black text-white italic mb-2">${asset.price}</div>
                  <button 
                    onClick={scrollToContact}
                    className="text-[10px] font-black uppercase tracking-[0.3em] text-[#DFFF00] hover:text-white transition-colors"
                  >
                    Order Asset <SafeIcon icon={FiArrowRight} className="inline ml-1" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pure Distribution Section */}
        <div className="mb-32">
          <div className="flex items-center gap-4 mb-12">
            <h3 className="text-white text-2xl font-black uppercase tracking-widest italic">02. Pure Distribution</h3>
            <span className="h-px flex-grow bg-white/10" />
            <span className="text-slate-500 text-[10px] font-black uppercase tracking-widest">Client Provides Content</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {distributionTiers.map((t, i) => (
              <div key={i} className={`p-[1px] ${t.highlight ? 'bg-[#DFFF00]' : 'bg-white/10'}`}>
                <div className="bg-[#0b0b0b] p-10 h-full flex flex-col">
                  <div className="mb-8">
                    <h4 className="text-[#DFFF00] font-black uppercase text-xs tracking-[0.3em] mb-4">{t.accounts} Account Nodes</h4>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-black text-white italic">${t.price}</span>
                      <span className="text-slate-500 font-bold uppercase text-xs">/mo</span>
                    </div>
                    <p className="text-slate-500 text-[10px] font-bold uppercase mt-4 tracking-widest">+ ${t.setup} SETUP FEE</p>
                  </div>
                  <ul className="space-y-4 mb-10 flex-grow">
                    <li className="flex items-center gap-3 text-[10px] font-black uppercase text-slate-300"><SafeIcon icon={FiCheck} className="text-[#DFFF00]" /> Single Network Deployment</li>
                    <li className="flex items-center gap-3 text-[10px] font-black uppercase text-slate-300"><SafeIcon icon={FiCheck} className="text-[#DFFF00]" /> Fresh Identity Protocol</li>
                    <li className="flex items-center gap-3 text-[10px] font-black uppercase text-slate-300"><SafeIcon icon={FiCheck} className="text-[#DFFF00]" /> Daily Content Distribution</li>
                    <li className="flex items-center gap-3 text-[10px] font-black uppercase text-slate-300"><SafeIcon icon={FiCheck} className="text-[#DFFF00]" /> Advanced IP Masking</li>
                  </ul>
                  <button 
                    onClick={scrollToContact}
                    className={`w-full py-4 text-[10px] font-black uppercase tracking-[0.3em] transition-all ${t.highlight ? 'bg-[#DFFF00] text-black hover:scale-[1.02]' : 'border border-white/20 text-white hover:bg-white hover:text-black'}`}
                  >
                    Initialize <SafeIcon icon={FiArrowRight} className="inline ml-2" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Content Section */}
        <div>
          <div className="flex items-center gap-4 mb-12">
            <h3 className="text-white text-2xl font-black uppercase tracking-widest italic">03. AI Forging + Distribution</h3>
            <span className="h-px flex-grow bg-white/10" />
            <span className="text-slate-500 text-[10px] font-black uppercase tracking-widest">Full-Stack Solution</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {aiTiers.map((t, i) => (
              <div key={i} className={`p-[1px] ${t.highlight ? 'bg-[#DFFF00]' : 'bg-white/10'}`}>
                <div className="bg-[#0b0b0b] p-10 h-full flex flex-col border border-[#DFFF00]/20">
                  <div className="mb-8">
                    <h4 className="text-[#DFFF00] font-black uppercase text-xs tracking-[0.3em] mb-4">{t.accounts} AI Nodes</h4>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-black text-white italic">${t.price}</span>
                      <span className="text-slate-500 font-bold uppercase text-xs">/mo</span>
                    </div>
                    <p className="text-slate-500 text-[10px] font-bold uppercase mt-4 tracking-widest">+ ${t.setup} SETUP FEE</p>
                  </div>
                  <ul className="space-y-4 mb-10 flex-grow">
                    <li className="flex items-center gap-3 text-[10px] font-black uppercase text-slate-300"><SafeIcon icon={FiCheck} className="text-[#DFFF00]" /> Industrial AI Video Forging</li>
                    <li className="flex items-center gap-3 text-[10px] font-black uppercase text-slate-300"><SafeIcon icon={FiCheck} className="text-[#DFFF00]" /> 1,000+ Unique Monthly Assets</li>
                    <li className="flex items-center gap-3 text-[10px] font-black uppercase text-slate-300"><SafeIcon icon={FiCheck} className="text-[#DFFF00]" /> Voice Cloning & Deepfake Tech</li>
                    <li className="flex items-center gap-3 text-[10px] font-black uppercase text-slate-300"><SafeIcon icon={FiCheck} className="text-[#DFFF00]" /> Full Network Management</li>
                  </ul>
                  <button 
                    onClick={scrollToContact}
                    className={`w-full py-4 text-[10px] font-black uppercase tracking-[0.3em] transition-all ${t.highlight ? 'bg-[#DFFF00] text-black hover:scale-[1.02]' : 'border border-white/20 text-white hover:bg-white hover:text-black'}`}
                  >
                    Deploy Engine <SafeIcon icon={FiArrowRight} className="inline ml-2" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20 text-center p-12 border border-white/5 glass-panel">
          <p className="text-slate-500 text-xs font-black uppercase tracking-[0.2em]">
            Need multiple networks or 200+ nodes? 
            <button 
              onClick={scrollToContact}
              className="text-white hover:text-[#DFFF00] ml-2 underline"
            >
              Request Multi-Network Quote
            </button>
          </p>
        </div>
      </div>
    </section>
  );
}